import { useRef } from "react";
import { Button } from "@/components/ui/button";
import { Autorenew, Ban, Eye, Trash2, Undo2, Upload, X } from "@/components/ds/icons";
import { Tip } from "@/components/ds/Tip";
import { toast } from "sonner";
import type { CapacitacionesDocumentoSlot } from "@/types/capacitaciones-documento";

const MAX_SIZE_BYTES = 50 * 1024 * 1024; // 50MB

const ACCEPT_EXT = ".jpg,.jpeg,.png,.docx,.pdf,.xlsx";
const ACCEPT_MIME = [
  "image/jpeg",
  "image/png",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
];

function fileExtensionAllowed(filename: string): boolean {
  const ext = filename.slice(filename.lastIndexOf(".")).toLowerCase();
  return ACCEPT_EXT.split(",").includes(ext);
}

interface CapacitacionesDocumentoAccionesProps {
  slot: CapacitacionesDocumentoSlot;
  /** Descarga/eliminación/no-aplica en curso para ESTE slot. */
  busy: boolean;
  /** Subida en curso para ESTE slot — muestra progreso + Cancelar en vez de las acciones normales. */
  uploading: boolean;
  /** 0-100. Solo se usa cuando `uploading` es `true`. */
  uploadPercent: number;
  /** Expone el `data-tour` de la guía guiada (`capacitaciones-subir`) — el
   * panel lo activa SOLO en el primer slot renderizado, para no duplicar
   * anclas en las ~25 filas del catálogo. */
  tourAnchors?: boolean;
  /** Se dispara con el archivo YA validado (mime/tamaño) — el panel arranca
   * la subida inmediatamente (Subir y Reemplazar comparten este callback). */
  onPickFile: (file: File) => void;
  onCancelUpload: () => void;
  onDownload: () => void;
  onRequestDelete: () => void;
  /** Marca o revierte "No aplica" para este tipo del catálogo (slice A3). */
  onToggleNoAplica: () => void;
}

/**
 * Celda de acciones de una fila del catálogo de documentos institucionales.
 * Cuatro variantes excluyentes, según el diseño de "Documentos Empresa":
 *
 *   - vacía        → `Subir` (botón) + `block` marcar No aplica
 *   - subiendo     → porcentaje + `Cancelar`
 *   - con archivo  → `visibility` ver · `autorenew` reemplazar · `delete` eliminar
 *   - No aplica    → `Sí aplica` (revierte la marca)
 *
 * Los tres de "con archivo" son botones de ícono: el diseño saca las
 * etiquetas para que la columna respire. Por eso TODOS llevan `title` +
 * `aria-label` — sin texto visible, es lo único que los hace comprensibles y
 * accesibles.
 *
 * `Subir` y `Reemplazar` abren el picker del SO directamente, sin modal
 * intermedio, y comparten el mismo `onPickFile`.
 *
 * Ya NO expone "Capacitaciones IA": los programas se generan desde el módulo
 * Capacitaciones, no desde acá. Esta pantalla sólo decide qué documento sirve
 * como fuente (switch de la columna "Fuente de capacitación").
 *
 * No mezcla ese switch ni la vigencia — viven en sus propias columnas del
 * `DataTable` (ver `CapacitacionesDocumentosPanel`).
 */
export function CapacitacionesDocumentoAcciones({
  slot,
  busy,
  uploading,
  uploadPercent,
  tourAnchors = false,
  onPickFile,
  onCancelUpload,
  onDownload,
  onRequestDelete,
  onToggleNoAplica,
}: CapacitacionesDocumentoAccionesProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { documento, no_aplica: noAplica, label } = slot;

  function triggerPicker() {
    fileInputRef.current?.click();
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const selected = e.target.files?.[0];
    // Reset inmediato para que volver a elegir el MISMO archivo dispare `onChange` de nuevo.
    e.target.value = "";
    if (!selected) return;

    if (selected.size > MAX_SIZE_BYTES) {
      toast.error("El archivo no puede superar los 50 MB.");
      return;
    }

    const mimeOk = ACCEPT_MIME.includes(selected.type);
    const extOk = fileExtensionAllowed(selected.name);
    if (!mimeOk && !extOk) {
      toast.error("Solo se aceptan archivos PDF, DOCX, XLSX, JPG o PNG.");
      return;
    }

    onPickFile(selected);
  }

  // El prototipo muestra sólo un spinner mientras sube; acá se conserva el
  // porcentaje real + Cancelar porque la subida es de verdad (XHR con
  // progreso y AbortController), no una animación de 1,4 s.
  if (uploading) {
    return (
      <div className="flex shrink-0 items-center justify-end gap-2">
        <span className="text-xs tabular-nums text-muted-foreground">{uploadPercent}%</span>
        <Button variant="outline" size="xs" onClick={onCancelUpload}>
          <X className="h-3.5 w-3.5" />
          Cancelar
        </Button>
      </div>
    );
  }

  return (
    <div className="flex shrink-0 flex-wrap items-center justify-end gap-1">
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPT_EXT}
        className="hidden"
        onChange={handleFileChange}
      />

      {/* Slot marcado No aplica: la única acción posible es revertir la marca —
          el backend rechaza (422) tanto subir como marcar de nuevo. */}
      {noAplica ? (
        <Button variant="outline" size="xs" onClick={onToggleNoAplica} disabled={busy}>
          <Undo2 className="h-3.5 w-3.5" />
          Sí aplica
        </Button>
      ) : documento ? (
        <>
          <Tip texto="Ver documento">
            <Button
              variant="ghost"
              size="icon-sm"
              className="text-muted-foreground hover:text-foreground"
              disabled={busy}
              onClick={onDownload}
              aria-label={`Ver ${label}`}
            >
              <Eye className="h-3.5 w-3.5" />
            </Button>
          </Tip>
          <Tip texto="Reemplazar documento">
            <Button
              variant="ghost"
              size="icon-sm"
              className="text-muted-foreground hover:text-foreground"
              disabled={busy}
              onClick={triggerPicker}
              aria-label={`Reemplazar ${label}`}
              data-tour={tourAnchors ? "capacitaciones-subir" : undefined}
            >
              <Autorenew className="h-3.5 w-3.5" />
            </Button>
          </Tip>
          <Tip texto="Eliminar documento">
            <Button
              variant="ghost"
              size="icon-sm"
              className="text-muted-foreground hover:text-destructive"
              disabled={busy}
              onClick={onRequestDelete}
              aria-label={`Eliminar ${label}`}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </Tip>
        </>
      ) : (
        <>
          {/* Sin Tip: el botón ya dice "Subir". Repetirlo en un globo sólo tapa
              lo que el usuario ya está leyendo. */}
          <Button
            variant="outline"
            size="xs"
            disabled={busy}
            onClick={triggerPicker}
            aria-label={`Subir ${label}`}
            data-tour={tourAnchors ? "capacitaciones-subir" : undefined}
          >
            <Upload className="h-3.5 w-3.5" />
            Subir
          </Button>
          {/* El backend rechaza marcar No aplica si el slot ya tiene archivo,
              por eso esta acción sólo existe en la variante vacía. */}
          <Tip texto="No nos aplica">
            <Button
              variant="ghost"
              size="icon-sm"
              className="text-muted-foreground hover:text-foreground"
              disabled={busy}
              onClick={onToggleNoAplica}
              aria-label={`Marcar ${label} como no aplica`}
            >
              <Ban className="h-3.5 w-3.5" />
            </Button>
          </Tip>
        </>
      )}
    </div>
  );
}
